import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { Asset } from '../../types/asset.types';
import assetService from '../../api/assetService';
import SongsMetadataEditor from '../common/SongsMetadataEditor';
import StarsMetadataEditor from '../common/StarsMetadataEditor';
import LooksInformation from '../common/LooksInformation';
import { AlgorhythmMetadataDisplay } from '../algorhythm/AlgorhythmMetadataDisplay';
import { DEFAULT_STARS_METADATA, DEFAULT_SONGS_METADATA } from '../../types/referencelayerMetadata';

interface ComponentMetadataSectionsProps {
  componentIds: string[];
  compositeAsset: Asset;
}

/**
 * Displays metadata for all 5 component assets in a Composite
 * Fetches component data dynamically (no duplication in DB)
 * All fields are read-only - to edit, user must navigate to source component
 */
export const ComponentMetadataSections: React.FC<ComponentMetadataSectionsProps> = ({
  componentIds,
  compositeAsset,
}) => {
  const [components, setComponents] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchComponents();
  }, [componentIds]);

  const fetchComponents = async () => {
    try {
      setLoading(true);
      setError(null);

      const fetched = await Promise.all(
        componentIds.map(id => assetService.getAssetById(id))
      );

      if (fetched.length > 0) {
        // Sort by layer order: G, S, L, M, W
        const layerOrder = { G: 1, S: 2, L: 3, M: 4, W: 5 };
        const sorted = fetched.sort((a: Asset, b: Asset) => {
          return (layerOrder[a.layer as keyof typeof layerOrder] || 999) -
                 (layerOrder[b.layer as keyof typeof layerOrder] || 999);
        });

        // Debug logging
        console.log('🔍 [ComponentMetadataSections] Fetched components:', sorted.length);
        sorted.forEach(comp => {
          console.log(`  - ${comp.layer} layer (${comp.name}):`, {
            hasSongMetadata: !!comp.songMetadata,
            hasStarMetadata: !!comp.starMetadata,
            hasLooksMetadata: !!comp.looksMetadata,
            hasMovesMetadata: !!comp.movesMetadata,
            hasWorldsMetadata: !!comp.worldsMetadata,
          });
        });

        setComponents(sorted);
      }
    } catch (err: any) {
      console.error('Error fetching component assets:', err);
      setError(err.message || 'Failed to load component metadata');
    } finally {
      setLoading(false);
    }
  };

  const getComponentByLayer = (layer: string): Asset | undefined => {
    return components.find(c => c.layer === layer);
  };

  const getEditLink = (component: Asset): string => {
    return `/assets/${component._id || component.id}/edit`;
  };

  if (loading) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Loading component metadata...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2 }}>
        {error}
      </Alert>
    );
  }

  if (components.length === 0) {
    return (
      <Alert severity="warning" sx={{ mt: 2 }}>
        No component assets found
      </Alert>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
        📦 Component Metadata (Read-Only)
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Metadata from all 5 component assets. To edit, click "Edit Source" to navigate to the original component asset.
      </Typography>

      {/* Song/Remix Metadata (G layer) */}
      {getComponentByLayer('G') && (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              🎵 Song/Remix Metadata ({getComponentByLayer('G')?.name})
            </Typography>
            <Button
              size="small"
              variant="outlined"
              endIcon={<OpenInNewIcon />}
              href={getEditLink(getComponentByLayer('G')!)}
              target="_blank"
            >
              Edit Source
            </Button>
          </Box>
          <SongsMetadataEditor
            layerMetadata={{
              ...DEFAULT_SONGS_METADATA,
              ...getComponentByLayer('G')?.songMetadata,
            } as any}
            asset={getComponentByLayer('G')!}
            isEditable={false}
            onMetadataChange={() => {}}
          />
          {getComponentByLayer('G')?.algorhythmMetadata && (
            <Box sx={{ mt: 2 }}>
              <AlgorhythmMetadataDisplay
                metadata={getComponentByLayer('G')!.algorhythmMetadata as any}
                title="Algorhythm Metadata"
              />
            </Box>
          )}
        </Paper>
      )}

      {/* Star Metadata (S layer) */}
      {getComponentByLayer('S') && (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              ⭐ Star Metadata ({getComponentByLayer('S')?.name})
            </Typography>
            <Button
              size="small"
              variant="outlined"
              endIcon={<OpenInNewIcon />}
              href={getEditLink(getComponentByLayer('S')!)}
              target="_blank"
            >
              Edit Source
            </Button>
          </Box>
          <StarsMetadataEditor
            layerMetadata={{
              ...DEFAULT_STARS_METADATA,
              ...getComponentByLayer('S')?.starMetadata,
            } as any}
            asset={getComponentByLayer('S')!}
            isEditable={false}
            onMetadataChange={() => {}}
          />
          {getComponentByLayer('S')?.algorhythmMetadata && (
            <Box sx={{ mt: 2 }}>
              <AlgorhythmMetadataDisplay
                metadata={getComponentByLayer('S')!.algorhythmMetadata as any}
                title="Algorhythm Metadata"
              />
            </Box>
          )}
        </Paper>
      )}

      {/* Look Metadata (L layer) */}
      {getComponentByLayer('L') && (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              👗 Look Metadata ({getComponentByLayer('L')?.name})
            </Typography>
            <Button
              size="small"
              variant="outlined"
              endIcon={<OpenInNewIcon />}
              href={getEditLink(getComponentByLayer('L')!)}
              target="_blank"
            >
              Edit Source
            </Button>
          </Box>
          <LooksInformation
            asset={getComponentByLayer('L')!}
            isEditable={false}
          />
          {getComponentByLayer('L')?.algorhythmMetadata && (
            <Box sx={{ mt: 2 }}>
              <AlgorhythmMetadataDisplay
                metadata={getComponentByLayer('L')!.algorhythmMetadata as any}
                title="Algorhythm Metadata"
              />
            </Box>
          )}
        </Paper>
      )}

      {/* Moves Metadata (M layer) */}
      {getComponentByLayer('M') && (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              💃 Moves Metadata ({getComponentByLayer('M')?.name})
            </Typography>
            <Button
              size="small"
              variant="outlined"
              endIcon={<OpenInNewIcon />}
              href={getEditLink(getComponentByLayer('M')!)}
              target="_blank"
            >
              Edit Source
            </Button>
          </Box>
          {getComponentByLayer('M')?.movesMetadata ? (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Dance Style: {getComponentByLayer('M')?.movesMetadata?.danceStyle || 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Difficulty: {getComponentByLayer('M')?.movesMetadata?.difficultyLevel || 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Energy Level: {getComponentByLayer('M')?.movesMetadata?.energyLevel || 'N/A'}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              No moves metadata available
            </Typography>
          )}
          {getComponentByLayer('M')?.algorhythmMetadata && (
            <Box sx={{ mt: 2 }}>
              <AlgorhythmMetadataDisplay
                metadata={getComponentByLayer('M')!.algorhythmMetadata as any}
                title="Algorhythm Metadata"
              />
            </Box>
          )}
        </Paper>
      )}

      {/* World Metadata (W layer) */}
      {getComponentByLayer('W') && (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
              🌍 World Metadata ({getComponentByLayer('W')?.name})
            </Typography>
            <Button
              size="small"
              variant="outlined"
              endIcon={<OpenInNewIcon />}
              href={getEditLink(getComponentByLayer('W')!)}
              target="_blank"
            >
              Edit Source
            </Button>
          </Box>
          {getComponentByLayer('W')?.worldsMetadata ? (
            <Box>
              <Typography variant="body2" color="text.secondary">
                Environment: {getComponentByLayer('W')?.worldsMetadata?.environmentType || 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Setting: {getComponentByLayer('W')?.worldsMetadata?.setting || 'N/A'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Mood: {getComponentByLayer('W')?.worldsMetadata?.mood || 'N/A'}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              No world metadata available
            </Typography>
          )}
          {getComponentByLayer('W')?.algorhythmMetadata && (
            <Box sx={{ mt: 2 }}>
              <AlgorhythmMetadataDisplay
                metadata={getComponentByLayer('W')!.algorhythmMetadata as any}
                title="Algorhythm Metadata"
              />
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default ComponentMetadataSections;
